'use client';

import PlusIconSvg from '@/assets/svgs/plus.svg';
import UsersFilledSvg from '@/assets/svgs/main/users-filled.svg';
import {
  useAssignUserToAccount,
  useFetchAssignedUsers,
  useUnassignUserFromAccount,
} from '@/containers/Accounts/api';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import { useFetchUsers } from '@/containers/Users/api';
import { User } from '@/containers/Users/types';
import {
  canUnassignUser,
  getUnassignTooltip,
} from '@/containers/Users/utils/userAccountUtils';
import { useAppDispatch } from '@/lib/redux/store';
import { AssignmentList } from '@/shared/components/AssignmentList';
import { SearchBar } from '@/shared/components/SearchBar/SearchBar';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modals } from '../all-modals';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { addOpenModal } from '../redux/modals.slice';
import { AssignUsersModalParams, ModalProps } from '../types';
import useModals from '../useModals';
import UserItemView from './UserItemView';

interface AssignUsersModalProps extends ModalProps {
  params?: AssignUsersModalParams;
}

const modalName = Modals.AssignUsersModal;

const AssignUsersModal = ({
  className,
  params,
  variant = 'default',
}: AssignUsersModalProps) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [isSearchEmpty, setIsSearchEmpty] = useState(true);
  const { onClose, isModalOpen } = useModals({
    modalName,
  });

  const dispatch = useAppDispatch();
  const toast = useToast();
  const accountId = params?.accountId || '';

  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [sessionAssignments, setSessionAssignments] = useState<Set<string>>(new Set());

  // Fetch all users
  const {
    data: usersData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
  } = useFetchUsers({ accountId, query: searchText });

  // Fetch assigned users
  const {
    data: assignedUsersResponse,
    isLoading: isLoadingAssignedUsers,
    refetch: refetchAssignedUsers,
  } = useFetchAssignedUsers(accountId);

  // Mutations
  const assignUserMutation = useAssignUserToAccount();
  const unassignUserMutation = useUnassignUserFromAccount();

  // Fetch all pages on mount
  useEffect(() => {
    if (hasNextPage && !isLoadingUsers) {
      fetchNextPage();
    }
  }, [hasNextPage, isLoadingUsers, fetchNextPage]);

  // Refetch when needed
  useEffect(() => {
    if (shouldRefresh) {
      refetchUsers();
      refetchAssignedUsers();
      setShouldRefresh(false);
    }
  }, [shouldRefresh, refetchUsers, refetchAssignedUsers]);

  // Flatten all users from pages
  const allUsers = useMemo(() => {
    return usersData?.pages.flatMap((page) => page.items) || [];
  }, [usersData]);

  // Get assigned user IDs
  const assignedUserIds = useMemo(() => {
    return (assignedUsersResponse?.data || []).map((user) => user.user_id);
  }, [assignedUsersResponse]);

  // Search functionality
  useEffect(() => {
    setIsSearchEmpty(searchText === '');
    if (!searchText) {
      return setSearchResults(allUsers);
    }

    setSearchResults(
      allUsers.filter(
        (user) =>
          user.email.toLowerCase().includes(searchText.toLowerCase()) ||
          `${user.first_name} ${user.last_name}`
            .toLowerCase()
            .includes(searchText.toLowerCase())
      )
    );
  }, [searchText, allUsers]);

  // Separate assigned and available users
  const assignedUsers = useMemo(() => {
    return searchResults.filter((user) => assignedUserIds.includes(user.user_id));
  }, [searchResults, assignedUserIds]);

  const availableUsers = useMemo(() => {
    return searchResults.filter((user) => !assignedUserIds.includes(user.user_id));
  }, [searchResults, assignedUserIds]);

  const handleAssignUser = async (user: User) => {
    try {
      await assignUserMutation.mutateAsync({ userId: user.user_id, accountId });
      toast.showSuccess('User assigned successfully');
      // Add to session assignments to track that this user was assigned in this modal session
      setSessionAssignments((prev) => new Set(prev).add(user.user_id));
      setShouldRefresh(true);
    } catch (error) {
      toast.showError('Failed to assign user');
      console.error('Failed to assign user:', error);
    }
  };

  const handleUnassignUser = async (user: User) => {
    try {
      await unassignUserMutation.mutateAsync({ userId: user.user_id, accountId });
      toast.showSuccess('User unassigned successfully');
      setShouldRefresh(true);
    } catch (error) {
      toast.showError('Failed to unassign user');
      console.error('Failed to unassign user:', error);
    }
  };

  const handleAssignAll = async () => {
    for (const user of availableUsers) {
      await handleAssignUser(user);
    }
  };

  const unmount = () => {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => unmount, []);

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />
      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        isSearchEmpty={isSearchEmpty}
        variant={variant}
        onClose={onClose}
        shouldCloseOnEscape>
        <div className="flex h-full flex-col overflow-hidden">
          {/* Stick to top */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<UsersFilledSvg className="h-5 w-5" />}
            title="Users"
          />

          {/* Take up remaining space and enable inside-scrolling */}
          <ModalBody ref={modalBodyRef} className="px-5 !py-0">
            {/* Action button */}
            <div className="flex h-[84px] items-center justify-end">
              <button
                className="btn btn-primary btn-sm flex gap-1 pl-1.5"
                onClick={() => {
                  dispatch(
                    addOpenModal({
                      modalName: Modals.CreateAccountUserModal,
                      modalParams: JSON.stringify({
                        accountId,
                      }),
                    })
                  );
                }}>
                <PlusIconSvg className="h-5 w-5" />
                Create User
              </button>
            </div>

            {/* Search */}
            <div className="search-container mb-6">
              <SearchBar
                search={searchText}
                setSearch={setSearchText}
                placeholder="Search users..."
                debounceMs={500}
                ariaLabel="Search users"
                className="w-full"
              />
            </div>

            {/* Assignment List */}
            <AssignmentList
              assignedItems={assignedUsers}
              availableItems={availableUsers}
              LineItemView={(props) => (
                <UserItemView
                  {...props}
                  onUserNameClick={() =>
                    dispatch(
                      addOpenModal({
                        modalName: Modals.UserDetailsModal,
                        modalParams: JSON.stringify({ userId: props.data.user_id }),
                      })
                    )
                  }
                />
              )}
              onAssign={handleAssignUser}
              onUnassign={handleUnassignUser}
              onAssignAll={handleAssignAll}
              isLoading={isLoadingUsers || isLoadingAssignedUsers}
              searchText={searchText}
              canUnassignItem={(user) =>
                canUnassignUser(user, {
                  accountId,
                  completedAssignments: sessionAssignments,
                })
              }
              getUnassignTooltip={(user) => {
                const canUnassign = canUnassignUser(user, {
                  accountId,
                  completedAssignments: sessionAssignments,
                });
                return getUnassignTooltip(canUnassign);
              }}
              emptyMessage={
                <div className="flex flex-col gap-3">
                  <span>No Users found</span>
                  {searchText && (
                    <button
                      className="btn btn-outline btn-sm border-gray-200 shadow-sm"
                      onClick={() => setSearchText('')}>
                      Reset search
                    </button>
                  )}
                  <button
                    className="btn btn-outline btn-sm border-gray-200 shadow-sm"
                    onClick={() =>
                      dispatch(
                        addOpenModal({
                          modalName: Modals.CreateAccountUserModal,
                          modalParams: JSON.stringify({
                            accountId,
                          }),
                        })
                      )
                    }>
                    Create User
                  </button>
                </div>
              }
              getItemKey={(user) => user.user_id}
            />
          </ModalBody>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </ModalContainer>
    </div>
  );
};

export default AssignUsersModal;
